import Aparecer from './Aparecer';
import Acordeao from './Acordeao';
import type { LojaRel } from '@/pages/gerenciar/painel/financeiro/relatorio/types';
import { formatarCentavos } from '@/pages/loja/utils';
import { horaBR, textoDiferenca, corDiferenca } from '@/pages/gerenciar/painel/financeiro/relatorio/utils';

const ROTULO_ORIGEM: Record<string, string> = { caixa: 'Caixa', online: 'Online' };
const ROTULO_METODO: Record<string, string> = {
  dinheiro: 'Dinheiro',
  pix: 'Pix',
  cartao: 'Cartão',
  debito: 'Débito',
  credito: 'Crédito',
  '-': 'Outro',
};

function Mini({
  rotulo,
  valor,
  cor,
}: {
  rotulo: string;
  valor: string;
  cor?: string;
}) {
  return (
    <div className="rounded-xl bg-background-50 px-3 py-2.5">
      <p className="text-[11px] uppercase tracking-wide text-foreground-500">{rotulo}</p>
      <p className={`mt-0.5 text-[14px] font-bold tabular-nums ${cor ?? 'text-foreground-950'}`}>
        {valor}
      </p>
    </div>
  );
}

export default function SecaoLoja({ loja }: { loja: LojaRel }) {
  const taxas = loja.totais.taxa_plataforma_centavos + loja.totais.taxa_gateway_centavos;
  const caixaMetodos = loja.por_metodo.filter((m) => m.origem === 'caixa');
  const onlineMetodos = loja.por_metodo.filter((m) => m.origem !== 'caixa');

  return (
    <Aparecer>
      <Acordeao
        titulo="Loja e caixa"
        resumo={`${formatarCentavos(loja.totais.vendido_centavos)} · ${loja.totais.pedidos} pedidos`}
      >
        <div className="space-y-5">
          {/* Resumo */}
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            <Mini rotulo="Vendido" valor={formatarCentavos(loja.totais.vendido_centavos)} />
            <Mini rotulo="Taxas" valor={formatarCentavos(taxas)} />
            <Mini rotulo="Líquido" valor={formatarCentavos(loja.totais.liquido_centavos)} />
          </div>

          {/* Por meio */}
          <div>
            <h3 className="text-[13px] font-bold uppercase tracking-wide text-foreground-500">
              Por meio
            </h3>
            {loja.por_metodo.length === 0 ? (
              <p className="mt-2 text-[13px] text-foreground-500">Sem vendas registradas.</p>
            ) : (
              <ul className="mt-2 divide-y divide-background-100">
                {[...caixaMetodos, ...onlineMetodos].map((m, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 py-2 text-[13px]">
                    <span className="flex min-w-0 items-baseline gap-1.5">
                      <span className="font-medium text-foreground-800">
                        {ROTULO_METODO[m.metodo] ?? m.metodo}
                      </span>
                      <span className="text-[11px] text-foreground-400">
                        {ROTULO_ORIGEM[m.origem] ?? m.origem} · {m.pedidos}
                      </span>
                    </span>
                    <span className="shrink-0 font-semibold tabular-nums text-foreground-900">
                      {formatarCentavos(m.total_centavos)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {loja.sangrias > 0 && (
              <p className="mt-1.5 text-[12px] text-foreground-500">
                Sangrias no total: − {formatarCentavos(loja.sangrias)}
              </p>
            )}
          </div>

          {/* Por barraca */}
          <div>
            <h3 className="text-[13px] font-bold uppercase tracking-wide text-foreground-500">
              Por barraca
            </h3>
            {loja.por_barraca.length === 0 ? (
              <p className="mt-2 text-[13px] text-foreground-500">Nenhuma barraca com ficha vendida.</p>
            ) : (
              <div className="mt-2 space-y-4">
                {loja.por_barraca.map((b, i) => (
                  <div key={i}>
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                      <p className="text-[14px] font-bold text-foreground-900">{b.barraca}</p>
                      <p className="text-[12px] tabular-nums text-foreground-600">
                        Vendido {b.vendido_qtd} · {formatarCentavos(b.vendido_centavos)} · Entregue{' '}
                        {b.retirado_qtd} · Falta {b.a_retirar_qtd}
                      </p>
                    </div>
                    {b.produtos.length > 0 && (
                      <ul className="mt-1.5 divide-y divide-background-100">
                        {b.produtos.map((p, k) => (
                          <li
                            key={k}
                            className="flex items-center justify-between gap-3 py-1.5 text-[13px]"
                          >
                            <span className="truncate text-foreground-800">{p.produto}</span>
                            <span className="shrink-0 tabular-nums text-foreground-600">
                              {p.qtd} · {formatarCentavos(p.valor_centavos)} · entregue {p.retirado}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Por caixa */}
          <div>
            <h3 className="text-[13px] font-bold uppercase tracking-wide text-foreground-500">
              Por caixa
            </h3>
            {loja.por_caixa.length === 0 ? (
              <p className="mt-2 text-[13px] text-foreground-500">Nenhum caixa aberto neste evento.</p>
            ) : (
              <div className="mt-2 space-y-4">
                {loja.por_caixa.map((c, i) => {
                  const aberto = !c.fechado_em;
                  const dinheiro = c.resumo?.dinheiro_centavos ?? c.dinheiro_centavos;
                  return (
                    <div key={i} className="rounded-xl border border-background-100 p-3">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                        <p className="text-[14px] font-bold text-foreground-900">
                          {c.caixa || 'Caixa'} · {c.operador || '—'}
                        </p>
                        <p className="text-[12px] text-foreground-500">
                          {horaBR(c.aberto_em)} — {aberto ? 'aberto' : horaBR(c.fechado_em)}
                        </p>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[13px] sm:grid-cols-3">
                        <Linha rotulo="Troco inicial" valor={formatarCentavos(c.troco_inicial_centavos)} />
                        <Linha
                          rotulo="Vendas em dinheiro"
                          valor={formatarCentavos(dinheiro)}
                          extra={c.resumo ? `${c.resumo.dinheiro_vendas} vendas` : undefined}
                        />
                        <Linha
                          rotulo="Sangrias"
                          valor={`− ${formatarCentavos(c.sangria_centavos)}`}
                          cor="text-danger-700"
                        />
                        <Linha rotulo="Esperado" valor={formatarCentavos(c.esperado_centavos)} />
                        <Linha
                          rotulo="Contado"
                          valor={c.contado_centavos == null ? '—' : formatarCentavos(c.contado_centavos)}
                        />
                        <Linha
                          rotulo="Diferença"
                          valor={textoDiferenca(c.diferenca_centavos, aberto)}
                          cor={corDiferenca(c.diferenca_centavos, aberto)}
                        />
                      </div>
                      <p className="mt-1.5 text-[11px] text-foreground-500">
                        Pix {formatarCentavos(c.resumo?.pix_centavos ?? 0)} · Cartão{' '}
                        {formatarCentavos(c.resumo?.cartao_centavos ?? 0)} · Desfeitas{' '}
                        {c.resumo?.desfeitas ?? 0}
                      </p>
                      {c.sangrias.length > 0 && (
                        <ul className="mt-1.5 text-[12px] text-foreground-600">
                          {c.sangrias.map((s, j) => (
                            <li key={j} className="flex justify-between gap-2">
                              <span className="truncate">
                                Sangria {horaBR(s.em)}
                                {s.observacao ? ` · ${s.observacao}` : ''}
                              </span>
                              <span className="shrink-0 tabular-nums">
                                − {formatarCentavos(s.valor_centavos)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Acordeao>
    </Aparecer>
  );
}

function Linha({ rotulo, valor, extra, cor }: { rotulo: string; valor: string; extra?: string; cor?: string }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-foreground-500">{rotulo}</p>
      <p className={`font-bold tabular-nums ${cor ?? 'text-foreground-900'}`}>{valor}</p>
      {extra && <p className="text-[11px] text-foreground-500">{extra}</p>}
    </div>
  );
}